import { GameSession } from '@/services/game-session'
import { GameDemoSession } from '@/services/game-session-providers/game-session-demo'
import type { GamePlayData, GameSessionPageData, InstantWinDraw } from '@/types'
import type { InstantGameSessionServiceInterface } from '@core/vue-plugins/instant-games/types'
import type { InstantGameSessionEventSource } from 'src/types'

import type ApiClient from '../lib/ApiClient'
import { createLogger } from '../lib/log'

const { log } = createLogger('InstantGameSessionService')

export default class InstantGameSessionService
  implements InstantGameSessionServiceInterface, InstantGameSessionEventSource
{
  constructor(private apiClient: ApiClient) {}

  private readonly events = new EventTarget()

  async play(id: string, count: number) {
    log(`[Session:${id}] Playing ${count} entries`)
    // Use existing GameDemoSession to handle demo mode
    if (id === this.demoSessionIdentifier) {
      return GameDemoSession.playGame(count)
    }

    const result = await this.apiClient.post<GamePlayData>(
      `/game-sessions/${id}/play`,
      { id, count },
      { auth: true }
    )
    if (result.remaining_plays === 0) {
      this.events.dispatchEvent(new CustomEvent('sessioncomplete'))
    }
    return result
  }

  createSession(_instantGame: InstantWinDraw): Promise<GameSessionPageData> {
    // Current API doesn't permit programmatic session creation. This is handled
    // automatically by the API when processing a payment
    throw new Error('Not implemented')
  }

  getGameSessionById(id: string) {
    log(`Loading session '${id}'`)
    return this.apiClient.get(`/game-sessions/${id}`, { auth: true })
  }

  supportsDemoSession(instantGame: InstantWinDraw) {
    return GameSession.supportsDemoSession(instantGame)
  }

  createDemoSession(instantGame: InstantWinDraw) {
    log('Creating demo session')
    return Promise.resolve(GameSession.createDemoSession(instantGame))
  }

  isDemoSession(session: GameSessionPageData): boolean {
    return (
      GameSession.demoSessionIdentifier === session.game_session.game_session_id
    )
  }

  onSessionComplete(listener: () => void) {
    this.events.addEventListener('sessioncomplete', listener)
    return () => this.events.removeEventListener('sessioncomplete', listener)
  }

  get demoSessionIdentifier() {
    return GameSession.demoSessionIdentifier
  }
}
